// Kennzahlen der Startseite. EIN Vertrag liefert die Kacheln einer Ansicht (Geschäftsführung, Filiale, Einkauf,
// Buchhaltung, Lager): Welche Ansichten es für eine Person gibt und welche Kacheln darin stehen, folgt allein ihren
// Rechten an ihren Standorten. Laufende Zahlen (offene Aufträge, fällige Posten, offene Schichten) kommen aus den
// Lese-Aktionen der Module, die sie führen – die Startseite zeigt nie eine eigene Rechnung dafür.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { DASHBOARD_PROFILES } from '../lib/labels'
import { READ_DASHBOARD, zDataState, zDay, zLocationRef } from './common'

export const zDashboardTile = z.object({
  /** stabile Kennung: 'revenue.today', 'receivables.overdue' … */
  id: z.string(),
  label: z.string(),
  /** Erläuterung der Kennzahl in einem Satz */
  info: z.string().nullable(),
  /** welches der drei Wertfelder gilt */
  format: z.enum(['money', 'count', 'percent']),
  amountCents: z.int().nullable(),
  count: z.int().nullable(),
  /** Anteil in Basispunkten (3140 = 31,4 %) */
  rateBp: z.int().nullable(),
  /** fertiger Zusatz unter dem Wert: „4 heute fällig“, „K01 offen seit 08:02 Uhr“ */
  hint: z.string().nullable(),
  tone: z.enum(['neutral', 'success', 'warning', 'danger']),
  /** Vergleich mit dem Zeitraum davor; null, wenn es keinen Vergleichswert gibt */
  delta: z
    .object({
      /** Veränderung in Basispunkten; bei `format: percent` in Prozentpunkten × 100 */
      changeBp: z.int(),
      /** „ggü. Dienstag der Vorwoche“ */
      label: z.string(),
      /** ob eine Zunahme gut ist (Umsatz) oder schlecht (überfällige Forderungen) */
      upIsGood: z.boolean(),
    })
    .nullable(),
  /** Verlauf der letzten Tage (älteste zuerst) in der Einheit des Werts; null ohne Verlauf */
  sparkline: z.array(z.int()).nullable(),
  /** Aufteilung des Werts (Altersstruktur, je Standort) */
  breakdown: z.array(z.object({ label: z.string(), amountCents: z.int().nullable(), count: z.int().nullable(), tone: z.enum(['neutral', 'success', 'warning', 'danger']) })).nullable(),
  /** Fortschritt (Zählfortschritt) */
  meter: z.object({ value: z.int(), max: z.int(), label: z.string() }).nullable(),
  /** wohin die Kachel führt */
  href: z.string().nullable(),
  /** 'primary' = große Kennzahl, 'secondary' = Zeile darunter */
  rank: z.enum(['primary', 'secondary']),
})

export const dashboardGet = defineContract({
  name: 'reporting.dashboard.get',
  title: 'Kennzahlen der Startseite anzeigen',
  description: 'Die wichtigsten Kennzahlen für die eigene Aufgabe: Umsatz, Rohertrag, offene Vorgänge, Forderungen, Bestand – je nach Rechten.',
  llm: {
    description:
      'Liefert die Kennzahlen der Startseite als Kacheln mit Wert (Cent, Anzahl oder Anteil in Basispunkten), Vergleich und Hinweis. `profile` wählt die Ansicht: ' +
      'management (Umsatz heute/Woche/Monat, Rohertrag, offene Aufträge, überfällige Forderungen, Lagerwert, Artikel unter Meldebestand, Ware kurz vor dem Verfallsdatum), ' +
      'store (Umsatz der Filiale heute gegen denselben Wochentag der Vorwoche, Durchschnittsbon, offene Schicht, Nachschub), purchasing, finance, warehouse. ' +
      'Ohne Angabe die Ansicht, die zu den Rechten passt. Es erscheinen nur Kacheln, für die die Person die Rechte an ihren Standorten hat; Rohertrag und Lagerwert nur mit dem Recht auf Einstandspreise. ' +
      'Verwenden für „Wie läuft es heute?“, „Gib mir einen Überblick“. Für Einzelheiten die jeweilige Auswertung (reporting.sales.report, reporting.stock.report, reporting.finance.report).',
    keywords: ['Überblick', 'Kennzahlen', 'wie läuft es', 'Dashboard', 'Tagesumsatz', 'Start'],
  },
  input: z.strictObject({
    profile: z.enum(DASHBOARD_PROFILES, { error: 'Bitte eine Ansicht aus der Liste wählen.' }).optional(),
    /** nur dieser Standort; ohne Angabe alle Standorte, an denen das Recht gilt */
    locationId: zId.optional(),
  }),
  output: z.object({
    profile: z.enum(DASHBOARD_PROFILES),
    profileLabel: z.string(),
    /** Ansichten, die diese Person öffnen kann */
    profiles: z.array(z.object({ key: z.enum(DASHBOARD_PROFILES), label: z.string() })),
    asOf: zDay,
    /** der Standort, für den die Zahlen gelten; null = alle eigenen Standorte */
    location: zLocationRef.nullable(),
    scopeLabel: z.string(),
    tiles: z.array(zDashboardTile),
    /** Umsatz je Tag der letzten 14 Tage mit dem Vergleich der 14 Tage davor; fehlt ohne Umsatzrecht */
    revenueSeries: z.array(z.object({ date: zDay, revenueCents: z.int(), comparisonRevenueCents: z.int() })).nullable(),
    /** Umsatz je Standort heute; fehlt, wenn die Person nur einen Standort sieht */
    locations: z
      .array(z.object({ id: zId, code: z.string(), name: z.string(), revenueCents: z.int(), documentCount: z.int(), weekRevenueCents: z.int(), changeBp: z.int().nullable() }))
      .nullable(),
    data: zDataState,
  }),
  permission: READ_DASHBOARD,
  risk: 'read',
  scope: (input) => (input.locationId ? [input.locationId] : 'any'),
})
